import type { CanvasPoint, Dimension, Shape } from './types';

export type Bounds = {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
};

type DimensionLayout = {
  extensionStart: CanvasPoint;
  extensionStartOffset: CanvasPoint;
  extensionEnd: CanvasPoint;
  extensionEndOffset: CanvasPoint;
};

export const getShapeBounds = (shape: Shape): Bounds => {
  if (shape.kind === 'circle') {
    return {
      minX: shape.center.x - shape.radius,
      minY: shape.center.y - shape.radius,
      maxX: shape.center.x + shape.radius,
      maxY: shape.center.y + shape.radius,
    };
  }

  if (shape.kind === 'arc') {
    return {
      minX: shape.center.x - shape.radius,
      minY: shape.center.y - shape.radius,
      maxX: shape.center.x + shape.radius,
      maxY: shape.center.y + shape.radius,
    };
  }

  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;

  for (let index = 0; index < shape.points.length; index += 2) {
    const x = shape.points[index];
    const y = shape.points[index + 1];
    if (x === undefined || y === undefined) continue;
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  }

  return { minX, minY, maxX, maxY };
};

export const getDimensionBounds = (
  dimension: Dimension,
  getDimensionLayout: (
    start: CanvasPoint,
    end: CanvasPoint,
    offset: number,
  ) => DimensionLayout | null,
): Bounds => {
  const layout = getDimensionLayout(dimension.start, dimension.end, dimension.offset);
  if (!layout) {
    return {
      minX: dimension.start.x,
      minY: dimension.start.y,
      maxX: dimension.end.x,
      maxY: dimension.end.y,
    };
  }

  return {
    minX: Math.min(
      layout.extensionStart.x,
      layout.extensionStartOffset.x,
      layout.extensionEnd.x,
      layout.extensionEndOffset.x,
    ),
    minY: Math.min(
      layout.extensionStart.y,
      layout.extensionStartOffset.y,
      layout.extensionEnd.y,
      layout.extensionEndOffset.y,
    ),
    maxX: Math.max(
      layout.extensionStart.x,
      layout.extensionStartOffset.x,
      layout.extensionEnd.x,
      layout.extensionEndOffset.x,
    ),
    maxY: Math.max(
      layout.extensionStart.y,
      layout.extensionStartOffset.y,
      layout.extensionEnd.y,
      layout.extensionEndOffset.y,
    ),
  };
};

export const getSelectionBounds = (start: CanvasPoint, end: CanvasPoint): Bounds => ({
  minX: Math.min(start.x, end.x),
  minY: Math.min(start.y, end.y),
  maxX: Math.max(start.x, end.x),
  maxY: Math.max(start.y, end.y),
});

export const boundsIntersect = (first: Bounds, second: Bounds) =>
  first.minX <= second.maxX &&
  first.maxX >= second.minX &&
  first.minY <= second.maxY &&
  first.maxY >= second.minY;
